import projectConfig from "../../projectConfig.json";
import { VisibilityOptionsState } from "../../appRedux/reduxInterfaces/ReducersInterfaces";
import { NormalButtonInterface } from "./FormV2Interfaces";

type MoveForwardVisibilityConfig = boolean | { clientTypeIds: string[] };

/**
 * Sorts an array of buttons based on the `priority` field within the `restrictions` property.
 * - Buttons with lower `priority` values are placed earlier in the list.
 * - Buttons without a valid `priority` (null, undefined, or NaN) are placed at the end of the list.
 * - If no button has a valid `priority`, the original list is returned unmodified.
 * - If the input is not an array, the original input is returned unmodified.
 *
 * @param {Array} buttons - The array of button objects to be sorted.
 * Each button object is expected to have the structure:
 * `{ restrictions: { priority: number | null | undefined } }`.
 *
 * @returns {Array} A new array of buttons sorted by `priority`.
 * If the input is not an array or if no valid `priority` exists, the original input is returned.
 *
 * @example
 * const buttons = [
 *   { restrictions: { priority: 3 } },
 *   { restrictions: { priority: 1 } },
 *   { restrictions: { priority: null } },
 *   { restrictions: { priority: 2 } },
 * ];
 *
 * const sortedButtons = sortByPriority(buttons);
 * // Result: [
 * //   { restrictions: { priority: 1 } },
 * //   { restrictions: { priority: 2 } },
 * //   { restrictions: { priority: 3 } },
 * //   { restrictions: { priority: null } },
 * // ]
 */

export const sortByPriority = (buttons: NormalButtonInterface) => {
   // cheks if buttons are array, if not, return oryginal buttons.
   if (!Array.isArray(buttons)) {
      return buttons;
   }

   // check if any button has value in "priority" field
   const hasPriority = buttons.some(
      (button) =>
         button?.restrictions?.priority !== null &&
         button?.restrictions?.priority !== undefined &&
         !isNaN(button?.restrictions?.priority)
   );

   // if not, return oryginal list.
   if (!hasPriority) {
      return buttons;
   }

   // creating buttons array copy
   return [...buttons].sort((a, b) => {
      const priorityA = a?.restrictions?.priority;
      const priorityB = b?.restrictions?.priority;

      // if any element don`t have priority, put him on end of the list
      if (priorityA === null || priorityA === undefined || isNaN(priorityA)) {
         return 1;
      }
      if (priorityB === null || priorityB === undefined || isNaN(priorityB)) {
         return -1;
      }

      return priorityA - priorityB;
   });
};

/**
 * Checks if the form is in a widget by analyzing the URL parameters.
 *
 * @param {string} url - The URL to be checked.
 * @returns {boolean} - Returns `true` if the form is in a widget (i.e., "processId" parameter is not present), otherwise returns `false`.
 */
export const isFormInWidget = (url) => {
   const searchParams = new URL(url).searchParams;
   return searchParams.has("processId") ? false : true;
};

/**
 * Filters out the buttons from the given list based on their visibility options (java).
 * If a visibility option for a button is false, the button will be removed from the list.
 *
 * @param {string[]} buttons - The list of button names to be filtered.
 * @param {VisibilityOptionsState} visibilityOptions - An object containing the visibility status of the buttons.
 * @returns {string[]} A filtered list of buttons where the visibility options are true.
 */
export const hideSystemButtons = (
   buttons: string[],
   visibilityOptions: VisibilityOptionsState
): string[] => {
   if (!buttons || !visibilityOptions) return buttons;

   // if you want to hide another button, just write it down alike others
   const visibilityMap: { [key: string]: boolean } = {
      askQuestion: visibilityOptions.isAskQuestionVisible,
      moveForward: visibilityOptions.isForwardVisible
   };

   const filteredButtons = buttons.filter((button) => visibilityMap[button] !== false);

   return filteredButtons;
};

export const normalizeSystemButtons = (
   systemButtons: Record<string, any> | Array<{ name?: string }> | null | undefined,
): Array<{ key: string; value: any }> => {
   if (!systemButtons) return [];

   if (Array.isArray(systemButtons)) {
      return systemButtons
         .filter((button) => typeof button?.name === "string")
         .map((button) => ({ key: button.name as string, value: button }));
   }

   return Object.entries(systemButtons).map(([key, value]) => ({ key, value }));
};

export const getCurrentClientTypeId = (
   departments: any[] | undefined,
   currentDepartmentId: string | number | null | undefined,
): string | undefined => {
   const currentDepartment =
      departments?.find(
         (department) => department?.id?.toString() === currentDepartmentId?.toString(),
      ) ?? departments?.find((department) => department?.default_department?.toString() === "1");

   return currentDepartment?.client_data?.kli_slo_typ?.toString();
};

export const isMoveForwardEnabledForClientType = (
   clientTypeId: string | null | undefined,
): boolean => {
   const config = projectConfig?.moveForwardVisibility as
      | MoveForwardVisibilityConfig
      | undefined;

   if (typeof config === "boolean") return config;

   if (config && Array.isArray(config.clientTypeIds)) {
      if (!clientTypeId) return false;

      return config.clientTypeIds.map(String).includes(clientTypeId.toString());
   }

   return true;
};

export const getVisibleSystemButtons = ({
   systemButtons,
   visibilityOptions,
   clientTypeId,
}: {
   systemButtons: Record<string, any> | Array<{ name?: string }> | null | undefined;
   visibilityOptions: VisibilityOptionsState;
   clientTypeId: string | null | undefined;
}): Array<{ key: string; value: any }> => {
   return normalizeSystemButtons(systemButtons).filter(({ key }) => {
      if (!hideSystemButtons([key], visibilityOptions)?.length) return false;

      if (key === "moveForward" && !isMoveForwardEnabledForClientType(clientTypeId)) {
         return false;
      }

      return true;
   });
};
